import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Mail, ExternalLink, Send, Loader2, Check, X, Plug, AlertTriangle } from "lucide-react";
import Modal from "@/components/Modal";
import { Btn, Input, Textarea, Select, Field, Badge } from "@/components/common";
import { api, apiError } from "@/lib/api";

const newKey = () => Math.random().toString(36).slice(2) + Date.now();

// Settings card — connect your own mailbox over SMTP (Proton Business, or any SMTP server).
export function EmailSettingsCard({ workspaceId, canEdit }) {
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({
    from_email: "", from_name: "", host: "smtp.protonmail.ch", port: 587,
    username: "", password: "", security: "starttls",
  });
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.get(`/email/status?workspace_id=${workspaceId}`).then((r) => setStatus(r.data)).catch(() => {});
  }, [workspaceId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("gmail") === "connected") { toast.success("Gmail connected"); window.history.replaceState({}, "", window.location.pathname); load(); }
    if (p.get("gmail") === "error") { toast.error("Gmail connection failed — please retry"); window.history.replaceState({}, "", window.location.pathname); }
  }, [load]);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const connectSmtp = async () => {
    if (!form.from_email.trim() || !form.password.trim()) return toast.error("From address and password/token are required");
    setBusy(true);
    try {
      const payload = { ...form, username: (form.username || form.from_email).trim(), port: Number(form.port) || 587 };
      const { data } = await api.post(`/email/smtp/connect?workspace_id=${workspaceId}`, payload);
      toast.success(`Mailbox connected — ${data.email}`);
      setForm((f) => ({ ...f, password: "" }));
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Could not connect mailbox"); }
    finally { setBusy(false); }
  };
  const connectGmail = async () => {
    try { const { data } = await api.get(`/oauth/gmail/login?workspace_id=${workspaceId}`); window.location.href = data.authorization_url; }
    catch (err) { toast.error(apiError(err.response?.data?.detail) || "Gmail is not configured on the server yet"); }
  };
  const disconnect = async () => {
    if (!window.confirm("Disconnect this mailbox? You can reconnect any time.")) return;
    try { await api.post(`/email/disconnect?workspace_id=${workspaceId}`); toast.success("Disconnected"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  if (!status) return null;

  return (
    <div className="mb-8">
      <div className="si-label flex items-center gap-1.5 mb-2"><Mail size={13} /> Email — send from your own mailbox</div>
      <div className="p-5" style={{ background: "var(--si-surface)", border: "1px solid var(--si-border)", borderRadius: 2 }} data-testid="email-settings-card">
        {status.connected ? (
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="flex items-center gap-2">
                <Badge value="confirmed">Connected</Badge>
                <span className="text-sm" style={{ color: "var(--si-text)" }}>{status.email}</span>
                <span className="si-label">{status.provider === "smtp" ? `SMTP · ${status.smtp_host || ""}` : "Gmail"}</span>
              </div>
              <div className="si-label mt-1">Sending from your account. Messages are recorded on the contact's correspondence timeline.</div>
            </div>
            {canEdit && <Btn variant="ghost" onClick={disconnect} data-testid="email-disconnect"><X size={14} /> Disconnect</Btn>}
          </div>
        ) : (
          <div>
            <div className="flex items-center gap-2 mb-3"><Badge value="disputed">Not connected</Badge></div>
            <div className="flex items-start gap-2 text-xs mb-4" style={{ color: "var(--si-text-2)" }}>
              <AlertTriangle size={14} style={{ color: "var(--si-warning)", flexShrink: 0, marginTop: 2 }} />
              <span>Enter your mailbox's SMTP details to send mail directly from your own account. For <strong>Proton</strong>, generate an SMTP token under <em>Proton → Settings → IMAP/SMTP → SMTP tokens</em> (available on Proton paid plans with a custom‑domain address) and paste it as the password. Credentials are encrypted at rest and never included in backups or exports.</span>
            </div>
            {canEdit ? (
              <div className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="From address"><Input value={form.from_email} onChange={upd("from_email")} placeholder="you@yourdomain.com" data-testid="smtp-from-email" /></Field>
                  <Field label="Display name (optional)"><Input value={form.from_name} onChange={upd("from_name")} placeholder="Strategic Interest" data-testid="smtp-from-name" /></Field>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <Field label="SMTP server"><Input value={form.host} onChange={upd("host")} data-testid="smtp-host" /></Field>
                  <Field label="Port"><Input type="number" value={form.port} onChange={upd("port")} data-testid="smtp-port" /></Field>
                  <Field label="Security"><Select value={form.security} onChange={upd("security")} data-testid="smtp-security"><option value="starttls">STARTTLS (587)</option><option value="ssl">SSL/TLS (465)</option></Select></Field>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="SMTP username" hint="Usually your full email address."><Input value={form.username} onChange={upd("username")} placeholder="you@yourdomain.com" data-testid="smtp-username" /></Field>
                  <Field label="Password / SMTP token"><Input type="password" value={form.password} onChange={upd("password")} placeholder="paste token" data-testid="smtp-password" /></Field>
                </div>
                <Btn variant="primary" onClick={connectSmtp} disabled={busy} data-testid="smtp-connect">
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <Plug size={14} />} {busy ? "Verifying…" : "Connect mailbox"}
                </Btn>
                {status.gmail_configured && (
                  <div className="pt-1"><button onClick={connectGmail} className="si-label underline" data-testid="email-connect">or connect a Google / Gmail account instead</button></div>
                )}
              </div>
            ) : (
              <div className="text-sm" style={{ color: "var(--si-text-2)" }}>Ask the workspace owner or an editor to connect a mailbox.</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Email button shown wherever a contact's email appears.
export function EmailButton({ workspaceId, entity, emails = [], className = "", size = 14, label = "Email" }) {
  const [open, setOpen] = useState(false);
  const has = (emails || []).filter(Boolean).length > 0;
  return (
    <>
      <Btn variant="ghost" className={className} onClick={() => setOpen(true)} data-testid="email-contact-btn"><Mail size={size} /> {label}</Btn>
      {open && <ComposeModal workspaceId={workspaceId} entity={entity} emails={emails} onClose={() => setOpen(false)} />}
    </>
  );
}

export function ComposeModal({ workspaceId, entity, emails = [], onClose }) {
  const valid = (emails || []).filter(Boolean);
  const [status, setStatus] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [to, setTo] = useState(valid.length === 1 ? valid[0] : "");
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [subject, setSubject] = useState("");
  const [bodyText, setBodyText] = useState("");
  const [makeFollow, setMakeFollow] = useState(false);
  const [followTitle, setFollowTitle] = useState("");
  const [followDate, setFollowDate] = useState("");
  const [sendState, setSendState] = useState("idle"); // idle | sending | sent | failed
  const [result, setResult] = useState(null);
  const keyRef = React.useRef(newKey());

  useEffect(() => {
    api.get(`/email/status?workspace_id=${workspaceId}`).then((r) => setStatus(r.data)).catch(() => {});
    api.get(`/email/templates?workspace_id=${workspaceId}`).then((r) => setTemplates(r.data || [])).catch(() => {});
  }, [workspaceId]);

  const applyTemplate = (id) => {
    const t = templates.find((x) => x.id === id);
    if (!t) return;
    const sub = (t.subject || "").replace(/\{\{name\}\}/g, entity?.name || "");
    const bd = (t.body || "").replace(/\{\{name\}\}/g, entity?.name || "");
    setSubject(sub); setBodyText(bd);
  };

  const mailto = () => {
    const params = new URLSearchParams();
    if (cc) params.set("cc", cc);
    if (bcc) params.set("bcc", bcc);
    if (subject) params.set("subject", subject);
    if (bodyText) params.set("body", bodyText);
    window.location.href = `mailto:${encodeURIComponent(to)}?${params.toString()}`;
  };

  const send = async () => {
    if (!to.trim()) return toast.error("Enter a recipient");
    setSendState("sending");
    try {
      const { data } = await api.post(`/email/send?workspace_id=${workspaceId}`, {
        idempotency_key: keyRef.current,
        to: to.split(",").map((s) => s.trim()).filter(Boolean),
        cc: cc.split(",").map((s) => s.trim()).filter(Boolean),
        bcc: bcc.split(",").map((s) => s.trim()).filter(Boolean),
        subject, body: bodyText, entity_ids: entity?.id ? [entity.id] : [],
        make_follow_up: makeFollow, follow_up_title: followTitle, follow_up_date: followDate || null,
      });
      setResult(data); setSendState("sent");
      toast.success(data.duplicate ? "Already sent (no duplicate created)" : "Email sent");
    } catch (err) {
      setSendState("failed");
      toast.error(apiError(err.response?.data?.detail) || "Send failed");
    }
  };

  const connected = status?.connected;

  return (
    <Modal open onClose={onClose} title={`Email${entity?.name ? ` — ${entity.name}` : ""}`} width={620} testid="email-compose-modal">
      {status && !connected && (
        <div className="mb-4 p-3 text-xs flex items-start gap-2" style={{ border: "1px solid var(--si-warning)", borderRadius: 2, color: "var(--si-text-2)" }} data-testid="email-not-connected">
          <AlertTriangle size={15} style={{ color: "var(--si-warning)", flexShrink: 0 }} />
          <span><strong style={{ color: "var(--si-warning)" }}>Email account not connected.</strong> Connect your mailbox in Settings to send in-app. Meanwhile use <strong>“Open in my email app”</strong> below — note these are NOT recorded as sent (the app can't verify them).</span>
        </div>
      )}
      {sendState === "sent" ? (
        <div className="py-8 text-center">
          <div className="mx-auto flex items-center justify-center mb-3" style={{ width: 52, height: 52, borderRadius: "50%", background: "rgba(16,185,129,0.15)", border: "1px solid var(--si-success)" }}><Check size={24} style={{ color: "var(--si-success)" }} /></div>
          <div className="text-sm" style={{ color: "var(--si-text)" }}>Sent{result?.message_id ? ` · ref ${String(result.message_id).slice(0, 10)}` : ""}.</div>
          <div className="si-label mt-1">Recorded on the correspondence timeline. Provider acceptance does not prove delivery or reading.</div>
          <Btn variant="primary" className="mt-5" onClick={onClose} data-testid="email-done">Done</Btn>
        </div>
      ) : (
        <div className="space-y-3">
          {valid.length === 0 && (
            <div className="text-xs p-2" style={{ color: "var(--si-text-2)", border: "1px dashed var(--si-border)", borderRadius: 2 }}>
              No email address on file for this contact. Add one on the Identity tab, or type a recipient below.
            </div>
          )}
          {valid.length > 1 ? (
            <Field label="To (pick an address)"><Select value={to} onChange={(e) => setTo(e.target.value)} data-testid="email-to-select"><option value="">— select —</option>{valid.map((em) => <option key={em} value={em}>{em}</option>)}</Select></Field>
          ) : (
            <Field label="To"><Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="name@example.com" data-testid="email-to" /></Field>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Cc"><Input value={cc} onChange={(e) => setCc(e.target.value)} data-testid="email-cc" /></Field>
            <Field label="Bcc"><Input value={bcc} onChange={(e) => setBcc(e.target.value)} data-testid="email-bcc" /></Field>
          </div>
          {templates.length > 0 && (
            <Field label="Template (optional)"><Select onChange={(e) => applyTemplate(e.target.value)} data-testid="email-template"><option value="">— none —</option>{templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}</Select></Field>
          )}
          <Field label="Subject"><Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="email-subject" /></Field>
          <Field label="Message"><Textarea value={bodyText} onChange={(e) => setBodyText(e.target.value)} style={{ minHeight: 160 }} data-testid="email-body" /></Field>
          <label className="flex items-center gap-2 text-sm" style={{ color: "var(--si-text)" }}>
            <input type="checkbox" checked={makeFollow} onChange={(e) => setMakeFollow(e.target.checked)} data-testid="email-followup" /> Create a follow-up task after sending
          </label>
          {makeFollow && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input value={followTitle} onChange={(e) => setFollowTitle(e.target.value)} placeholder="Follow-up title" data-testid="email-followup-title" />
              <Input type="date" value={followDate} onChange={(e) => setFollowDate(e.target.value)} data-testid="email-followup-date" />
            </div>
          )}
          <div className="flex items-center justify-between pt-1">
            <Btn variant="ghost" onClick={mailto} disabled={!to.trim()} data-testid="email-mailto"><ExternalLink size={14} /> Open in my email app</Btn>
            <Btn variant="primary" onClick={send} disabled={!connected || sendState === "sending"} data-testid="email-send">
              {sendState === "sending" ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} {sendState === "sending" ? "Sending…" : "Send"}
            </Btn>
          </div>
          {!connected && <div className="si-label text-right">In-app Send is disabled until your mailbox is connected.</div>}
        </div>
      )}
    </Modal>
  );
}
